import { Box, Space, SpaceVertical, type SpaceVerticalProps } from '@looker/components';
import { BINotification } from '@pulsepointinc/bi-components';
import type { DsInputCustomEvent } from '@pulsepointinc/design-system';
import { DsInput, DsTypography } from '@pulsepointinc/design-system-react';
import { type FC, useEffect, useState } from 'react';
import type { ICustomFilter } from '../../../../../../helpers/helpers';
import { useFilters } from '../../../../../../hooks/useFilters';
import type { Filter } from '../../../../../../stores/filters';

interface ChannelRecencyProps extends ICustomFilter, SpaceVerticalProps {
  onMultiFilterChange?: (filter: Filter) => void;
}

const filterName = 'smart_pixel_lookback_period_filter';

const defaultFilter: Filter = {
  filterName,
  expression: '7 days',
  enabled: true,
  expressionType: 'target',
};

const defaultInputValue = parseInt(defaultFilter.expression, 10);

const ChannelRecency: FC<ChannelRecencyProps> = ({ onMultiFilterChange, onChange, ...spaceProps }) => {
  const { filters } = useFilters();
  const filterExpression = filters[filterName]?.expression ?? '';

  const [inputValue, setInputValue] = useState<string | number>(defaultInputValue);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    if (onMultiFilterChange) {
      const filterToSend = filterExpression ? { ...defaultFilter, expression: filterExpression } : defaultFilter;
      onMultiFilterChange(filterToSend);
    }
  }, []);

  useEffect(() => {
    if (filterExpression) {
      const initialValue = parseInt(filterExpression, 10);
      setInputValue(initialValue);
    }
  }, [filterExpression]);

  const handleInputChange = (value: string | number) => {
    if (typeof value !== 'string' || !/^\d*$/.test(value)) {
      return;
    }

    const numericValue = value === '' ? '' : Math.min(Number(value), 365);

    setInputValue(numericValue);

    if (filterExpression) {
      setShowWarning(true);
    }

    const filter: Filter = {
      filterName,
      expression: numericValue === '' ? defaultFilter.expression : `${numericValue} days`,
      enabled: true,
      expressionType: defaultFilter.expressionType,
    };

    if (onMultiFilterChange) {
      onMultiFilterChange(filter);
    }
    if (onChange) {
      onChange(filter.expression);
    }
  };

  return (
    <SpaceVertical gap="u2" paddingX="u6" {...spaceProps}>
      <DsTypography variant="label">Recency</DsTypography>
      <Space gap="u2" align="center" width="auto">
        <Box width="80px">
          <DsInput
            type="text"
            onValueChange={(e: DsInputCustomEvent<string>) => handleInputChange(e.detail)}
            value={String(inputValue)}
          />
        </Box>
        <DsTypography>days</DsTypography>
      </Space>
      {showWarning && (
        <BINotification text="Channel recency will be carried across all Engagement filters" intent="warning" />
      )}
    </SpaceVertical>
  );
};

export default ChannelRecency;
